import React, { useState } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  House,
  ClipboardText,
  FileText,
  ListChecks,
  Warning,
  Folder,
  ChartBar,
  SignOut,
  List,
  X,
  Users,
  ChatCircleText,
  Package,
  Wrench
} from '@phosphor-icons/react';

export const Layout = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const navItems = [
    { path: '/dashboard', label: 'Dashboard', icon: House },
    { path: '/apqp', label: 'APQP', icon: ClipboardText },
    { path: '/ppap', label: 'PPAP', icon: FileText },
    { path: '/inspections', label: 'Inspections', icon: ListChecks },
    { path: '/defects', label: 'Defects', icon: Warning },
    { path: '/complaints', label: 'Complaints', icon: ChatCircleText },
    { path: '/8d-reports', label: '8D Reports', icon: Wrench },
    { path: '/fifo', label: 'FIFO Inventory', icon: Package },
    { path: '/documents', label: 'Documents', icon: Folder },
    { path: '/reports', label: 'Reports', icon: ChartBar },
  ];

  // Admin-only navigation items - check for admin role
  const adminNavItems = user?.role === 'admin' 
    ? [{ path: '/users', label: 'User Management', icon: Users }]
    : [];

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');

  return (
    <div className="min-h-screen bg-background">
      {/* Mobile header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-white border-b border-gray-200 flex items-center justify-between px-4 z-40">
        <img src="/images/alufin-logo.png" alt="ALU-FIN" className="h-10 w-auto object-contain" />
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="p-2 hover:bg-gray-100 rounded-sm transition-colors"
        >
          {sidebarOpen ? <X size={24} /> : <List size={24} />}
        </button>
      </div>

      {/* Sidebar */}
      <aside className={`fixed top-0 left-0 h-full w-64 bg-muted border-r border-gray-200 z-50 transition-transform lg:translate-x-0 ${
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      }`}>
        <div className="h-full flex flex-col">
          <div className="p-4 border-b border-gray-200">
            <img 
              src="/images/alufin-logo.png" 
              alt="ALU-FIN" 
              className="h-16 w-auto object-contain mx-auto"
            />
          </div>

          <nav className="flex-1 p-4 space-y-1">
            {navItems.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.path);
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                  onClick={() => setSidebarOpen(false)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-sm transition-colors ${
                    active
                      ? 'bg-gray-900 text-white'
                      : 'text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <Icon size={20} weight={active ? 'fill' : 'regular'} />
                  <span className="text-sm font-medium">{item.label}</span>
                </Link>
              );
            })}
            
            {adminNavItems.length > 0 && (
              <>
                <div className="border-t border-gray-300 my-2"></div>
                {adminNavItems.map((item) => {
                  const Icon = item.icon;
                  const active = isActive(item.path);
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                      onClick={() => setSidebarOpen(false)}
                      className={`flex items-center gap-3 px-4 py-3 rounded-sm transition-colors ${
                        active
                          ? 'bg-gray-900 text-white'
                          : 'text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      <Icon size={20} weight={active ? 'fill' : 'regular'} />
                      <span className="text-sm font-medium">{item.label}</span>
                    </Link>
                  );
                })}
              </>
            )}
          </nav>

          <div className="p-4 border-t border-gray-200">
            <div className="mb-3 p-3 bg-white rounded-sm border border-gray-200">
              <p className="text-xs uppercase tracking-wider text-gray-500 font-medium">Logged in as</p>
              <p className="text-sm font-semibold text-gray-900 mt-1">{user?.name}</p>
              <p className="text-xs text-gray-600 capitalize">{user?.role?.replace('_', ' ')}</p>
            </div>
            <button
              data-testid="logout-button"
              onClick={handleLogout}
              className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-gray-900 text-white rounded-sm hover:bg-gray-800 transition-colors"
            >
              <SignOut size={18} weight="bold" />
              <span className="text-sm font-medium">Sign Out</span>
            </button>
          </div>
        </div>
      </aside>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main content */}
      <main className="lg:ml-64 min-h-screen pt-16 lg:pt-0">
        {/* Desktop header with logo on right */}
        <div className="hidden lg:flex items-center justify-end px-8 py-4 border-b border-gray-200 bg-white">
          <img 
            src="/images/alufin-logo.png" 
            alt="ALU-FIN - Surface Coating Technology Experts Since 1978" 
            className="h-12 w-auto object-contain"
          />
        </div>
        <div className="p-6 lg:p-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
};
